// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import { SimError, ModelError, EquationError, ErrorCode, UnitError } from '@simlin/core/datamodel';
import { errorCodeDescription } from '@simlin/engine';

import styles from './ErrorDetails.module.css';

interface ErrorDetailsProps {
  simError: SimError | undefined;
  modelErrors: readonly ModelError[];
  varErrors: ReadonlyMap<string, readonly EquationError[]>;
  varUnitErrors: ReadonlyMap<string, readonly UnitError[]>;
  status: 'ok' | 'error' | 'disabled';
}

export function ErrorDetails({
  simError,
  modelErrors,
  varErrors,
  varUnitErrors,
}: ErrorDetailsProps): React.ReactElement {
  const errors = [];
  if (
    simError &&
    !(
      (simError.code === ErrorCode.NotSimulatable || simError.code === ErrorCode.EmptyEquation) &&
      modelErrors.length > 0
    )
  ) {
    errors.push(
      <div key="sim" className={styles.list}>
        simulation error: {errorCodeDescription(simError.code)}
      </div>,
    );
  }
  if (modelErrors.length > 0) {
    modelErrors.forEach((err, i) => {
      if (err.code === ErrorCode.VariablesHaveErrors && varErrors.size > 0) {
        return;
      }
      const details = err.details;
      errors.push(
        <div key={`model-${i}-${err.code}`} className={styles.list}>
          model error: {errorCodeDescription(err.code)}
          {details ? `: ${details}` : undefined}
        </div>,
      );
    });
  }
  for (const [ident, errs] of varErrors) {
    for (const err of errs) {
      errors.push(
        <div key={`var-${ident}-${err.code}-${err.start}`} className={styles.list}>
          variable "{ident}" error: {errorCodeDescription(err.code)}
        </div>,
      );
    }
  }
  for (const [ident, errs] of varUnitErrors) {
    for (const err of errs) {
      // Unit errors carry the bare reason in `details` (e.g. "computed units
      // 'x' don't match specified units"); the code description is only the
      // fallback for errors without one.
      errors.push(
        <div key={`unit-${ident}-${err.code}-${err.start}`} className={styles.list}>
          variable "{ident}" unit error: {err.details ?? errorCodeDescription(err.code)}
        </div>,
      );
    }
  }

  return (
    <div className={styles.card}>
      <div className={styles.inner}>
        {errors.length > 0 ? errors : <div className={styles.yay}>Your model is error free!</div>}
      </div>
    </div>
  );
}
